<template>
	<div class="d-flex align-center ga-2 ml-4">
		<SelectControl :model-value="selectedViewId" :label="t('UserViews')" :items="viewSelectItems" title-key="name" density="comfortable" :sort-items="false" :clearable="false" class="user-view-control__select" :disabled="!props.isReady || isApplying" @update:model-value="onSelectedViewChanged" />
		<v-tooltip :attach="overlayTarget" location="bottom" v-if="!isTablet">
			<template v-slot:activator="{ props: tooltipProps }">
				<span v-bind="tooltipProps">
					<v-btn density="comfortable" icon="add" variant="tonal" :disabled="!props.isReady || isSaving" @click="onFabAddClick" />
				</span>
			</template>
			<span>{{ t('Add') }}</span>
		</v-tooltip>
		<v-tooltip :attach="overlayTarget" location="bottom" v-if="!isTablet">
			<template v-slot:activator="{ props: tooltipProps }">
				<span v-bind="tooltipProps">
					<v-btn density="comfortable" icon="save" variant="tonal" :disabled="!props.isReady || !selectedPersistedView || !isSelectedViewDirty || isSaving" @click="onFabSaveClick" />
				</span>
			</template>
			<span>{{ t('Save') }}</span>
		</v-tooltip>
		<v-fab size="x-small" icon :color="fabOpen ? '' : 'primary'" flat variant="tonal">
			<v-tooltip :attach="overlayTarget" location="bottom">
				<template v-slot:activator="{ props: tooltipProps }">
					<v-icon v-bind="tooltipProps">{{ fabOpen ? 'close' : isTablet ? 'keyboard_arrow_down' : 'keyboard_arrow_right' }}</v-icon>
				</template>
				<span>{{ fabOpen ? t('Less') : t('More') }}</span>
			</v-tooltip>
			<v-speed-dial v-model="fabOpen" :location="isTablet ? 'bottom center' : 'right center'" transition="scale" activator="parent">
				<div key="add" v-if="isTablet">
					<v-tooltip :attach="overlayTarget" location="bottom">
						<template v-slot:activator="{ props: tooltipProps }">
							<span v-bind="tooltipProps">
								<v-btn color="success" icon="add" :disabled="!props.isReady || isSaving" @click="onFabAddClick" />
							</span>
						</template>
						<span>{{ t('Add') }}</span>
					</v-tooltip>
				</div>
				<div key="save" v-if="isTablet">
					<v-tooltip :attach="overlayTarget" location="bottom">
						<template v-slot:activator="{ props: tooltipProps }">
							<span v-bind="tooltipProps">
								<v-btn color="primary" icon="save" :disabled="!props.isReady || !selectedPersistedView || !isSelectedViewDirty || isSaving" @click="onFabSaveClick" />
							</span>
						</template>
						<span>{{ t('Save') }}</span>
					</v-tooltip>
				</div>
				<div key="edit">
					<v-tooltip :attach="overlayTarget" location="bottom">
						<template v-slot:activator="{ props: tooltipProps }">
							<span v-bind="tooltipProps">
								<v-btn color="info" icon="edit" :disabled="!props.isReady || !selectedPersistedView || isSaving" @click="onFabEditClick" />
							</span>
						</template>
						<span>{{ t('Edit') }}</span>
					</v-tooltip>
				</div>
				<div key="delete">
					<v-tooltip :attach="overlayTarget" location="bottom">
						<template v-slot:activator="{ props: tooltipProps }">
							<span v-bind="tooltipProps">
								<v-btn color="error" icon="delete" :disabled="!props.isReady || !selectedPersistedView || isSaving" @click="onFabDeleteClick" />
							</span>
						</template>
						<span>{{ t('Delete') }}</span>
					</v-tooltip>
				</div>
			</v-speed-dial>
		</v-fab>
	</div>

	<v-dialog v-model="dialogOpen" :max-width="'400px'" :attach="overlayTarget || undefined">
		<v-card>
			<v-card-title>
				<md-icon>table_eye</md-icon>
				{{ mode === 'add' ? t('AddUserView') : t('EditUserView') }}
			</v-card-title>
			<v-card-text>
				<v-text-field v-model="name" minlength="3" maxlength="50" counter :label="t('Name')" required variant="outlined" density="comfortable" />
			</v-card-text>
			<v-card-actions>
				<v-spacer></v-spacer>
				<v-btn text @click="dialogOpen = false" data-close>{{ t('Close') }}</v-btn>
				<v-btn variant="tonal" @click="saveView" :disabled="name.trim().length < 3 || isSaving">{{ t('Save') }}</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>

	<v-dialog v-model="deleteDialogOpen" :max-width="'420px'" :attach="overlayTarget || undefined">
		<v-card>
			<v-card-title>
				<md-icon>warning</md-icon>
				{{ t('Delete') }}
			</v-card-title>
			<v-card-text>{{ t('AreYouSureYouWantToDelete') }} {{ t('TheUserView') }} {{ selectedPersistedView?.name || '' }} ?</v-card-text>
			<v-card-actions>
				<v-spacer></v-spacer>
				<v-btn text @click="deleteDialogOpen = false">{{ t('Close') }}</v-btn>
				<v-btn color="error" variant="tonal" :disabled="isSaving" @click="deleteSelectedView">{{ t('Delete') }}</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<script setup>
import { inject, computed, ref, onMounted, onUnmounted, watch } from 'vue';
import { iframeDataBridge } from '@/iframe/iframeDataBridge';
import { useTranslator } from '@/utils/useTranslator';
import { getUserSettings, postUserSettings } from '@/api/core/userSetting';
import { log } from '@/utils/vueLogger.js';
import { deviceUtils } from '@/utils/deviceUtils';
import SelectControl from '@/components/controls/SelectControl.vue';

const DEFAULT_VIEW_ID = '__default__';

const props = defineProps({
	viewKey: { type: String, required: true },
	groupName: { type: String, default: 'WEBAPP-UserViews' },
	getViewState: { type: Function, default: null },
	applyViewState: { type: Function, default: null },
	defaultViewState: { type: [Object, Function], default: null },
	isReady: { type: Boolean, default: true },
	stateVersion: { type: Number, default: 0 },
});

const emit = defineEmits(['view-applied', 'view-saved', 'view-deleted', 'view-error']);

const t = useTranslator();
const user = iframeDataBridge.getCurrentUser();
const overlayTarget = inject('overlayTarget', null);
const isTablet = ref(deviceUtils.isTablet());
const onResize = () => {
	isTablet.value = deviceUtils.isTablet();
};

const dialogOpen = ref(false);
const deleteDialogOpen = ref(false);
const settings = ref([]);
const views = ref([]);
const selectedViewId = ref(null);
const mode = ref('add');
const name = ref('');
const editingViewId = ref(null);
const isSaving = ref(false);
const isApplying = ref(false);
const fabOpen = ref(false);
const pendingViewIdToApply = ref(null);
const defaultViewState = ref(null);
const isSelectedViewDirty = ref(false);

const defaultViewLabel = computed(() => {
	return t('DefaultView');
});
const viewSelectItems = computed(() => [{ id: DEFAULT_VIEW_ID, name: defaultViewLabel.value }, ...views.value.map((view) => ({ id: view.id, name: view.name }))]);
const selectedPersistedView = computed(() => views.value.find((view) => view.id === selectedViewId.value) || null);

onMounted(async () => {
	await loadViews();
	window.addEventListener('resize', onResize);
});

onUnmounted(() => {
	window.removeEventListener('resize', onResize);
});

watch(
	() => props.isReady,
	async (isReady) => {
		if (isReady && !defaultViewState.value) {
			const initialState = await captureCurrentViewState();
			if (initialState) {
				defaultViewState.value = initialState;
			}
		}

		if (!isReady || !pendingViewIdToApply.value) {
			return;
		}

		const viewId = pendingViewIdToApply.value;
		pendingViewIdToApply.value = null;
		await applySelectedView(viewId, { persistSelection: true });
		await refreshDirtyState();
	}
);

watch(
	() => props.stateVersion,
	async () => {
		await refreshDirtyState();
	}
);

watch(
	() => selectedViewId.value,
	async () => {
		await refreshDirtyState();
	}
);

function openAddViewDialog() {
	mode.value = 'add';
	editingViewId.value = null;
	name.value = '';
	dialogOpen.value = true;
}

function onFabAddClick() {
	fabOpen.value = false;
	openAddViewDialog();
}

function onFabSaveClick() {
	fabOpen.value = false;
	updateSelectedView();
}

function onFabEditClick() {
	fabOpen.value = false;
	openEditViewDialog();
}

function onFabDeleteClick() {
	fabOpen.value = false;
	deleteDialogOpen.value = true;
}

function openEditViewDialog() {
	if (!selectedPersistedView.value) {
		return;
	}

	mode.value = 'edit';
	editingViewId.value = selectedPersistedView.value.id;
	name.value = selectedPersistedView.value.name;
	dialogOpen.value = true;
}

function normalizeViews(rawValue) {
	if (!rawValue) {
		return [];
	}

	try {
		const parsed = typeof rawValue === 'string' ? JSON.parse(rawValue) : rawValue;
		if (!Array.isArray(parsed)) {
			return [];
		}

		return parsed
			.map((view) => {
				if (!view || typeof view !== 'object') {
					return null;
				}

				const normalizedName = String(view.name || '').trim();
				if (!normalizedName) {
					return null;
				}

				return {
					id: view.id || crypto.randomUUID(),
					name: normalizedName,
					state: view.state ?? null,
					createdAt: view.createdAt || new Date().toISOString(),
					updatedAt: view.updatedAt || new Date().toISOString(),
				};
			})
			.filter(Boolean);
	} catch {
		return [];
	}
}

async function loadViews() {
	try {
		settings.value = await getUserSettings({ groupName: props.groupName, userId: user.id });
		const viewSetting = (Array.isArray(settings.value) ? settings.value : []).find((setting) => setting.name === props.viewKey);
		views.value = normalizeViews(viewSetting?.value);

		if (props.isReady && !defaultViewState.value) {
			const initialState = await captureCurrentViewState();
			if (initialState) {
				defaultViewState.value = initialState;
			}
		}

		const lastSelectedViewId = getLastSelectedViewId();
		if (!lastSelectedViewId) {
			selectedViewId.value = DEFAULT_VIEW_ID;
			return;
		}

		const isDefaultSelection = lastSelectedViewId === DEFAULT_VIEW_ID;
		const hasPersistedSelection = views.value.some((view) => view.id === lastSelectedViewId);
		if (!isDefaultSelection && !hasPersistedSelection) {
			clearLastSelectedViewId();
			selectedViewId.value = null;
			return;
		}

		selectedViewId.value = lastSelectedViewId;
		if (isDefaultSelection) {
			await refreshDirtyState();
			return;
		}

		if (props.isReady) {
			await applySelectedView(lastSelectedViewId, { persistSelection: true });
		} else {
			pendingViewIdToApply.value = lastSelectedViewId;
		}

		await refreshDirtyState();
	} catch (error) {
		log('error', 'UserViewControl', 'Failed to load user views');
	}
}

function getStorageKey() {
	return `WEBAPP-UserViews:lastSelected:${user?.id || 'anonymous'}:${props.viewKey}`;
}

function getLastSelectedViewId() {
	try {
		return localStorage.getItem(getStorageKey());
	} catch {
		return null;
	}
}

function setLastSelectedViewId(viewId) {
	try {
		if (!viewId) {
			localStorage.removeItem(getStorageKey());
			return;
		}

		localStorage.setItem(getStorageKey(), String(viewId));
	} catch {
		// localStorage may be unavailable depending on browser context.
	}
}

function clearLastSelectedViewId() {
	setLastSelectedViewId(null);
}

function safeClone(value) {
	if (value === null || value === undefined) {
		return value;
	}

	try {
		return JSON.parse(JSON.stringify(value));
	} catch {
		return value;
	}
}

async function persistViews() {
	const nextSettings = (Array.isArray(settings.value) ? settings.value : []).filter((setting) => setting.name !== props.viewKey);
	nextSettings.push({
		userId: user.id,
		groupName: props.groupName,
		name: props.viewKey,
		value: JSON.stringify(views.value),
	});

	const isSaved = await postUserSettings(nextSettings, { groupName: props.groupName, userId: user.id });
	if (!isSaved) {
		log('error', 'UserViewControl', 'Failed to save user views');
		return false;
	}

	settings.value = nextSettings;
	return true;
}

async function captureCurrentViewState() {
	if (typeof props.getViewState !== 'function') {
		log('error', 'UserViewControl', 'Failed to save view: capture unavailable');
		return null;
	}

	try {
		return await props.getViewState();
	} catch (error) {
		log('error', 'UserViewControl', 'Failed to save current view');
		return null;
	}
}

function getNormalizedStateFingerprint(state) {
	if (state === null || state === undefined) {
		return '';
	}

	const normalizeObject = (value) => {
		if (Array.isArray(value)) {
			return value.map((entry) => normalizeObject(entry));
		}

		if (!value || typeof value !== 'object') {
			return value;
		}

		const normalized = {};
		Object.keys(value)
			.sort((left, right) => left.localeCompare(right))
			.forEach((key) => {
				normalized[key] = normalizeObject(value[key]);
			});

		return normalized;
	};

	return JSON.stringify(normalizeObject(state));
}

async function refreshDirtyState() {
	if (!props.isReady || isApplying.value || !selectedPersistedView.value) {
		isSelectedViewDirty.value = false;
		return;
	}

	const currentState = await captureCurrentViewState();
	if (!currentState) {
		isSelectedViewDirty.value = false;
		return;
	}

	const persistedFingerprint = getNormalizedStateFingerprint(selectedPersistedView.value.state);
	const currentFingerprint = getNormalizedStateFingerprint(currentState);
	isSelectedViewDirty.value = persistedFingerprint !== currentFingerprint;
}

async function saveView() {
	const normalizedName = name.value.trim();
	if (normalizedName.length < 3) {
		return;
	}

	isSaving.value = true;
	try {
		const state = await captureCurrentViewState();
		if (!state) {
			return;
		}

		const existingViewWithSameName = views.value.find((view) => view.name.toLowerCase() === normalizedName.toLowerCase() && view.id !== editingViewId.value);
		if (existingViewWithSameName) {
			log('error', 'UserViewControl', 'A view with this name already exists');
			return;
		}

		if (mode.value === 'edit' && editingViewId.value) {
			views.value = views.value.map((view) => {
				if (view.id !== editingViewId.value) {
					return view;
				}

				return {
					...view,
					name: normalizedName,
					state: state,
					updatedAt: new Date().toISOString(),
				};
			});
			selectedViewId.value = editingViewId.value;
			setLastSelectedViewId(editingViewId.value);
		} else {
			const createdView = {
				id: crypto.randomUUID(),
				name: normalizedName,
				state: state,
				createdAt: new Date().toISOString(),
				updatedAt: new Date().toISOString(),
			};
			views.value = [...views.value, createdView];
			selectedViewId.value = createdView.id;
			setLastSelectedViewId(createdView.id);
		}

		const saved = await persistViews();
		if (!saved) {
			return;
		}

		emit('view-saved', selectedPersistedView.value);
		dialogOpen.value = false;
		isSelectedViewDirty.value = false;
	} finally {
		isSaving.value = false;
	}
}

async function onSelectedViewChanged(viewId) {
	selectedViewId.value = viewId || null;

	if (!viewId) {
		clearLastSelectedViewId();
		isSelectedViewDirty.value = false;
		return;
	}

	if (!props.isReady) {
		setLastSelectedViewId(viewId);
		pendingViewIdToApply.value = viewId;
		isSelectedViewDirty.value = false;
		return;
	}

	await applySelectedView(viewId, { persistSelection: true });
	await refreshDirtyState();
}

async function applySelectedView(viewId, options = {}) {
	const persistSelection = options?.persistSelection !== false;
	const isDefaultView = viewId === DEFAULT_VIEW_ID;
	const targetView = views.value.find((view) => view.id === viewId);
	if ((!isDefaultView && !targetView) || typeof props.applyViewState !== 'function') {
		return;
	}

	isApplying.value = true;
	try {
		if (isDefaultView) {
			const defaultStateToApply = await resolveDefaultViewState();
			if (!defaultStateToApply) {
				return;
			}
			await props.applyViewState(safeClone(defaultStateToApply));
		} else {
			await props.applyViewState(safeClone(targetView.state));
		}

		if (persistSelection) {
			setLastSelectedViewId(viewId);
		}

		emit('view-applied', isDefaultView ? { id: DEFAULT_VIEW_ID, name: defaultViewLabel.value } : targetView);
		isSelectedViewDirty.value = false;
	} catch (error) {
		log('error', 'UserViewControl', 'Failed to apply user view');
	} finally {
		isApplying.value = false;
	}
}

async function resolveDefaultViewState() {
	if (typeof props.defaultViewState === 'function') {
		try {
			const resolvedState = await props.defaultViewState();
			if (resolvedState) {
				return resolvedState;
			}
		} catch (error) {
			log('error', 'UserViewControl', 'Failed to resolve default user view');
		}
	}

	if (props.defaultViewState && typeof props.defaultViewState === 'object') {
		return props.defaultViewState;
	}

	return defaultViewState.value || (await captureCurrentViewState());
}

async function updateSelectedView() {
	if (!selectedPersistedView.value) {
		return;
	}

	isSaving.value = true;
	try {
		const currentState = await captureCurrentViewState();
		if (!currentState) {
			return;
		}

		const selectedId = selectedPersistedView.value.id;
		views.value = views.value.map((view) => {
			if (view.id !== selectedId) {
				return view;
			}

			return {
				...view,
				state: currentState,
				updatedAt: new Date().toISOString(),
			};
		});

		const saved = await persistViews();
		if (!saved) {
			return;
		}

		emit('view-saved', selectedPersistedView.value);
		isSelectedViewDirty.value = false;
	} finally {
		isSaving.value = false;
	}
}

async function deleteSelectedView() {
	if (!selectedPersistedView.value) {
		deleteDialogOpen.value = false;
		return;
	}

	isSaving.value = true;
	try {
		const removedViewId = selectedPersistedView.value.id;
		views.value = views.value.filter((view) => view.id !== removedViewId);
		selectedViewId.value = DEFAULT_VIEW_ID;
		setLastSelectedViewId(DEFAULT_VIEW_ID);

		const saved = await persistViews();
		if (!saved) {
			return;
		}

		emit('view-deleted', removedViewId);
		deleteDialogOpen.value = false;
		isSelectedViewDirty.value = false;
	} finally {
		isSaving.value = false;
	}
}
</script>

<style scoped>
.user-view-control__select {
	min-width: 180px;
	max-width: 360px;
}
</style>
